import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { makeStyles, shorthands, mergeClasses, Spinner } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  BuildingMultiple20Regular,
  Building20Regular,
  Search20Regular,
  ChevronRight20Regular,
  Dismiss16Regular,
  Open16Regular,
  Checkmark16Regular,
} from "@fluentui/react-icons";
import { useSmartBack } from "../hooks/smartBack";
import { REFINED } from "../components/risikoanalyseStep/tokens";
import { FIRMA_CONN_ROLES } from "../components/relationsInline/types";
import { RelationContactPicker } from "../components/redesign/RelationContactPicker";
import { reverseFirmaLabel } from "../services/haushalteCleanupService";
import {
  listStructures,
  listStructureTeams,
  fetchStructureDetail,
  createFirmaConnection,
  saveStructureReview,
  type StructureListRow,
  type StructureDetail,
  type StructureCompany,
  type StructureStatus,
  type StructureSort,
  type StructureReviewItem,
} from "../services/unternehmensstrukturenCleanupService";

const PAGE = 30;

const useStyles = makeStyles({
  root: { maxWidth: "1100px", ...shorthands.margin("0", "auto"), ...shorthands.padding("18px", "20px", "120px") },
  bar: {
    display: "flex", alignItems: "center", columnGap: "12px", marginBottom: "14px",
    ...shorthands.padding("14px", "18px"),
    backgroundImage: `linear-gradient(180deg, ${REFINED.aub50}, ${REFINED.white})`,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("12px"),
  },
  barIcon: { color: REFINED.aub, flexShrink: 0 },
  title: { fontSize: "1.15rem", fontWeight: 700, color: REFINED.ink, margin: 0 },
  subtitle: { fontSize: "0.82rem", color: REFINED.ink2, marginTop: "2px" },
  filters: { display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: "10px", rowGap: "8px", marginBottom: "14px" },
  seg: { display: "inline-flex", ...shorthands.border("1px", "solid", REFINED.line), ...shorthands.borderRadius("8px"), overflow: "hidden" },
  segBtn: {
    ...shorthands.padding("6px", "12px"), fontSize: "0.8rem", color: REFINED.ink2, cursor: "pointer",
    backgroundColor: REFINED.white, ...shorthands.border("0", "none", "transparent"), borderRightWidth: "1px", borderRightStyle: "solid", borderRightColor: REFINED.line,
    ":last-child": { borderRightWidth: 0 },
  },
  segOn: { backgroundColor: REFINED.aub, color: REFINED.white, fontWeight: 600 },
  select: {
    ...shorthands.padding("7px", "10px"), fontSize: "0.8rem", color: REFINED.ink, backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line), ...shorthands.borderRadius("8px"), minWidth: "150px",
  },
  searchWrap: { position: "relative", display: "inline-flex", alignItems: "center", flexGrow: 1, minWidth: "200px" },
  searchIcon: { position: "absolute", left: "10px", color: REFINED.ink3, pointerEvents: "none" },
  search: {
    width: "100%", ...shorthands.padding("8px", "10px", "8px", "34px"), fontSize: "0.85rem", color: REFINED.ink,
    backgroundColor: REFINED.white, ...shorthands.border("1px", "solid", REFINED.line), ...shorthands.borderRadius("8px"),
  },
  count: { fontSize: "0.78rem", color: REFINED.ink3, marginBottom: "8px" },
  card: {
    display: "flex", alignItems: "center", columnGap: "12px", ...shorthands.padding("13px", "16px"), marginBottom: "8px",
    backgroundColor: REFINED.white, ...shorthands.border("1px", "solid", REFINED.line), ...shorthands.borderRadius("10px"),
    cursor: "pointer", transition: "border-color .12s, box-shadow .12s",
    ":hover": { ...shorthands.borderColor(REFINED.aub200), boxShadow: "0 2px 10px rgba(125,33,105,0.08)" },
  },
  cardMain: { minWidth: 0, flexGrow: 1 },
  cardAddr: { fontSize: "0.95rem", fontWeight: 600, color: REFINED.ink },
  cardCity: { fontSize: "0.8rem", color: REFINED.ink2, marginTop: "1px" },
  cardNames: { fontSize: "0.78rem", color: REFINED.ink3, marginTop: "5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  badge: {
    flexShrink: 0, display: "inline-flex", alignItems: "center", columnGap: "4px", ...shorthands.padding("3px", "9px"),
    backgroundColor: REFINED.aub50, color: REFINED.aub700, fontSize: "0.78rem", fontWeight: 700, ...shorthands.borderRadius("999px"),
  },
  pill: { flexShrink: 0, ...shorthands.padding("2px", "9px"), fontSize: "0.68rem", fontWeight: 700, ...shorthands.borderRadius("999px"), textTransform: "uppercase", letterSpacing: "0.03em" },
  pillOpen: { backgroundColor: REFINED.goldBg, color: REFINED.goldInk },
  pillDone: { backgroundColor: REFINED.okBg, color: REFINED.ok },
  // Detail
  backBtn: {
    display: "inline-flex", alignItems: "center", columnGap: "6px", ...shorthands.padding("6px", "10px"), marginBottom: "12px",
    fontSize: "0.82rem", color: REFINED.aub, cursor: "pointer", backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", REFINED.line), ...shorthands.borderRadius("8px"),
  },
  detailHead: { marginBottom: "6px" },
  detailAddr: { fontSize: "1.2rem", fontWeight: 700, color: REFINED.ink },
  detailCity: { fontSize: "0.88rem", color: REFINED.ink2, marginTop: "2px" },
  hint: {
    ...shorthands.padding("10px", "14px"), marginTop: "12px", marginBottom: "16px", fontSize: "0.82rem", color: REFINED.ink2,
    backgroundColor: REFINED.aub50, ...shorthands.border("1px", "solid", REFINED.aub100), ...shorthands.borderRadius("10px"),
  },
  firma: {
    ...shorthands.padding("14px", "16px"), marginBottom: "10px", backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line), ...shorthands.borderRadius("12px"),
  },
  firmaMuted: { opacity: 0.55, backgroundColor: REFINED.fieldBg },
  firmaTop: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", columnGap: "12px" },
  firmaName: { fontSize: "0.98rem", fontWeight: 700, color: REFINED.ink, display: "inline-flex", alignItems: "center", columnGap: "6px" },
  firmaMeta: { fontSize: "0.76rem", color: REFINED.ink3, marginTop: "2px" },
  cLink: { color: REFINED.aub, display: "inline-flex", alignItems: "center", ":hover": { color: REFINED.aub700 } },
  existing: { marginTop: "8px", display: "flex", flexWrap: "wrap", columnGap: "8px", rowGap: "5px" },
  existingChip: {
    display: "inline-flex", alignItems: "center", columnGap: "5px", ...shorthands.padding("3px", "9px"),
    fontSize: "0.75rem", color: REFINED.ink2, backgroundColor: REFINED.aub50, ...shorthands.borderRadius("999px"),
  },
  row: { display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: "8px", rowGap: "8px", marginTop: "12px" },
  rowSelect: {
    ...shorthands.padding("7px", "9px"), fontSize: "0.82rem", color: REFINED.ink, backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line), ...shorthands.borderRadius("8px"), minWidth: "150px",
  },
  shareInput: {
    width: "100px", ...shorthands.padding("7px", "9px"), fontSize: "0.82rem", color: REFINED.ink, backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line), ...shorthands.borderRadius("8px"),
  },
  targetChip: {
    display: "inline-flex", alignItems: "center", columnGap: "6px", ...shorthands.padding("6px", "10px"), fontSize: "0.82rem",
    color: REFINED.ink, backgroundColor: REFINED.aub50, ...shorthands.border("1px", "solid", REFINED.aub100), ...shorthands.borderRadius("8px"),
  },
  chipX: { cursor: "pointer", color: REFINED.ink3, display: "inline-flex", ":hover": { color: REFINED.err } },
  excludeLbl: { display: "inline-flex", alignItems: "center", columnGap: "6px", marginLeft: "auto", fontSize: "0.8rem", color: REFINED.ink2, cursor: "pointer", userSelect: "none" },
  pickerBox: { marginTop: "10px", ...shorthands.padding("10px"), backgroundColor: REFINED.fieldBg, ...shorthands.border("1px", "dashed", REFINED.line), ...shorthands.borderRadius("10px") },
  saveBar: {
    position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 20, display: "flex", alignItems: "center", justifyContent: "flex-end",
    columnGap: "12px", ...shorthands.padding("12px", "22px"), backgroundColor: "rgba(255,255,255,0.96)", borderTopWidth: "1px",
    borderTopStyle: "solid", borderTopColor: REFINED.line, backdropFilter: "blur(6px)",
  },
  saveInfo: { marginRight: "auto", fontSize: "0.82rem", color: REFINED.ink2 },
  btnPrimary: {
    ...shorthands.padding("9px", "20px"), fontSize: "0.85rem", fontWeight: 600, color: REFINED.white, backgroundColor: REFINED.aub,
    ...shorthands.border("0", "none", "transparent"), ...shorthands.borderRadius("8px"), cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub700 }, ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  errText: { color: REFINED.err, fontSize: "0.82rem" },
  empty: { textAlign: "center", color: REFINED.ink3, fontSize: "0.9rem", ...shorthands.padding("40px", "0") },
  center: { display: "flex", justifyContent: "center", ...shorthands.padding("30px", "0") },
});

/** UI-Formularstand je Firma. */
interface FormState {
  role: string;
  targetId: number | null;
  targetLabel: string;
  share: string;
  notBelongs: boolean;
  pickerOpen: boolean;
}

const emptyForm = (notBelongs: boolean): FormState => ({
  role: "",
  targetId: null,
  targetLabel: "",
  share: "",
  notBelongs,
  pickerOpen: false,
});

const addrLine = (s: string | null, p: string | null, o: string | null): string =>
  [s, [p, o].filter(Boolean).join(" ")].filter((x) => x && x.trim()).join(" · ");

const firmaLabel = (c: Pick<StructureCompany, "name" | "suffix">): string =>
  [c.name, c.suffix].filter((x) => x && x.trim()).join(" ").trim() || "—";

export default function UnternehmensstrukturenCleanupPage() {
  const s = useStyles();
  const goBack = useSmartBack("/datenbereinigung");

  // Filter
  const [status, setStatus] = useState<StructureStatus>("open");
  const [sort, setSort] = useState<StructureSort>("count");
  const [teamId, setTeamId] = useState<string>("");
  const [q, setQ] = useState<string>("");
  const [teams, setTeams] = useState<Array<{ id: string; name: string | null }>>([]);

  // Liste
  const [rows, setRows] = useState<StructureListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const offsetRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  // Detail
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<StructureDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [forms, setForms] = useState<Record<number, FormState>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listStructureTeams().then(setTeams).catch(() => setTeams([]));
  }, []);

  const loadList = useCallback(
    async (reset: boolean) => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      if (reset) offsetRef.current = 0;
      setLoading(true);
      try {
        const { rows: got, total: tot } = await listStructures(
          status, sort, teamId || undefined, q.trim() || undefined, PAGE, offsetRef.current, ctrl.signal,
        );
        setTotal(tot);
        setRows((prev) => (reset ? got : [...prev, ...got]));
        offsetRef.current += got.length;
      } catch (e) {
        if ((e as any)?.name !== "AbortError") setRows((prev) => (reset ? [] : prev));
      } finally {
        if (abortRef.current === ctrl) setLoading(false);
      }
    },
    [status, sort, teamId, q],
  );

  // Debounced Reload bei Filteränderung (nur in der Listenansicht).
  useEffect(() => {
    if (selected) return;
    const t = setTimeout(() => loadList(true), 250);
    return () => clearTimeout(t);
  }, [status, sort, teamId, q, selected, loadList]);

  const openDetail = useCallback(async (structureKey: string) => {
    setSelected(structureKey);
    setDetail(null);
    setForms({});
    setError(null);
    setDetailLoading(true);
    try {
      const d = await fetchStructureDetail(structureKey);
      setDetail(d);
      const init: Record<number, FormState> = {};
      for (const c of d.companies) init[c.id] = emptyForm(c.reviewStatus === "not_belongs");
      setForms(init);
    } catch (e) {
      setError((e as Error)?.message ?? "Detail konnte nicht geladen werden.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => {
    setSelected(null);
    setDetail(null);
    setForms({});
    setError(null);
    loadList(true); // Liste auffrischen (Status kann sich geändert haben)
  }, [loadList]);

  const patchForm = (cid: number, patch: Partial<FormState>) =>
    setForms((prev) => ({ ...prev, [cid]: { ...prev[cid], ...patch } }));

  // Dirty-Erkennung: geänderter Ausschluss ODER neue Verbindung.
  const dirtyCount = useMemo(() => {
    if (!detail) return 0;
    let n = 0;
    for (const c of detail.companies) {
      const f = forms[c.id];
      if (!f) continue;
      const wasExcluded = c.reviewStatus === "not_belongs";
      if (f.notBelongs !== wasExcluded) n++;
      else if (!f.notBelongs && f.role && f.targetId) n++;
    }
    return n;
  }, [detail, forms]);

  const onSave = useCallback(async () => {
    if (!detail) return;
    setSaving(true);
    setError(null);
    try {
      const reviewItems: StructureReviewItem[] = [];
      const conns: Array<{ firmaId: number; role: string; targetId: number; share: string }> = [];
      for (const c of detail.companies) {
        const f = forms[c.id];
        if (!f) continue;
        if (f.notBelongs) {
          reviewItems.push({ contactId: c.id, status: "not_belongs" });
        } else if (f.role && f.targetId) {
          conns.push({ firmaId: c.id, role: f.role, targetId: f.targetId, share: f.share });
          reviewItems.push({ contactId: c.id, status: "linked" });
        } else if (c.reviewStatus === "not_belongs") {
          // Ausschluss zurückgenommen, keine neue Verbindung → zurück auf offen.
          reviewItems.push({ contactId: c.id, status: null });
        }
      }
      // Erst Verbindungen anlegen (idempotent + Schwester-Kaskade), dann Marker.
      for (const conn of conns) {
        if (conn.firmaId === conn.targetId) continue;
        await createFirmaConnection(conn.role, conn.firmaId, conn.targetId, conn.share);
      }
      if (reviewItems.length) await saveStructureReview(detail.structureKey, reviewItems);
      await openDetail(detail.structureKey); // frisch laden (zeigt neue Verbindungen)
    } catch (e) {
      setError((e as Error)?.message ?? "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }, [detail, forms, openDetail]);

  // ── Detail-Ansicht ──────────────────────────────────────────────────────────
  if (selected) {
    return (
      <div className={s.root}>
        <button className={s.backBtn} onClick={closeDetail}>
          <ArrowLeft20Regular /> Zurück zur Übersicht
        </button>

        {detailLoading && (
          <div className={s.center}><Spinner size="small" label="Firmen werden geladen…" /></div>
        )}

        {detail && (
          <>
            <div className={s.detailHead}>
              <div className={s.detailAddr}>{detail.strasse || "—"}</div>
              <div className={s.detailCity}>
                {[detail.plz, detail.ort].filter(Boolean).join(" ")} · {detail.companies.length} Firmen
              </div>
            </div>
            <div className={s.hint}>
              Verknüpfe je Firma eine Verbindung (Tochter/Mutter/Schwester/Beteiligung) zu einer anderen
              Firma – oder markiere „gehört nicht zu dieser Adresse". Verbindungen werden ins
              Kontaktsystem geschrieben; Schwesterfirmen entstehen automatisch.
            </div>

            {detail.companies.map((c) => {
              const f = forms[c.id] ?? emptyForm(c.reviewStatus === "not_belongs");
              const others = detail.companies.filter((o) => o.id !== c.id && !(forms[o.id]?.notBelongs));
              return (
                <div key={c.id} className={mergeClasses(s.firma, f.notBelongs && s.firmaMuted)}>
                  <div className={s.firmaTop}>
                    <div>
                      <div className={s.firmaName}>
                        <Building20Regular style={{ color: REFINED.aub }} />
                        {firmaLabel(c)}
                        <a className={s.cLink} href={`/contacts/${c.id}`} target="_blank" rel="noreferrer" title="Kontakt öffnen">
                          <Open16Regular />
                        </a>
                      </div>
                      <div className={s.firmaMeta}>
                        {[c.contactNbrInt && `Nr. ${c.contactNbrInt}`, c.industry, addrLine(c.strasse, c.plz, c.ort)]
                          .filter(Boolean)
                          .join("  ·  ")}
                      </div>
                    </div>
                    <label className={s.excludeLbl}>
                      <input
                        type="checkbox"
                        checked={f.notBelongs}
                        onChange={(e) => patchForm(c.id, { notBelongs: e.target.checked })}
                      />
                      gehört nicht dazu
                    </label>
                  </div>

                  {c.connections.length > 0 && (
                    <div className={s.existing}>
                      {c.connections.map((conn) => {
                        const outgoing = conn.contactSrcId === c.id;
                        const label = outgoing ? conn.relationType : (reverseFirmaLabel(conn.relationType) ?? conn.relationType);
                        const other = outgoing ? conn.destName : conn.srcName;
                        return (
                          <span key={conn.id} className={s.existingChip}>
                            <Checkmark16Regular style={{ color: REFINED.ok }} />
                            {label}: {other || "—"}{conn.share ? ` (${conn.share})` : ""}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {!f.notBelongs && (
                    <>
                      <div className={s.row}>
                        <select
                          className={s.rowSelect}
                          value={f.role}
                          onChange={(e) => patchForm(c.id, { role: e.target.value })}
                        >
                          <option value="">Verbindung wählen…</option>
                          {FIRMA_CONN_ROLES.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>

                        {f.targetId ? (
                          <span className={s.targetChip}>
                            {f.targetLabel}
                            <span className={s.chipX} onClick={() => patchForm(c.id, { targetId: null, targetLabel: "", pickerOpen: false })}>
                              <Dismiss16Regular />
                            </span>
                          </span>
                        ) : (
                          <select
                            className={s.rowSelect}
                            value=""
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "__other__") { patchForm(c.id, { pickerOpen: true }); return; }
                              const id = Number(v);
                              const found = others.find((o) => o.id === id);
                              if (found) patchForm(c.id, { targetId: id, targetLabel: firmaLabel(found), pickerOpen: false });
                            }}
                          >
                            <option value="">Zielfirma wählen…</option>
                            {others.map((o) => (
                              <option key={o.id} value={o.id}>{firmaLabel(o)}</option>
                            ))}
                            <option value="__other__">andere Firma…</option>
                          </select>
                        )}

                        {f.role === "Beteiligung" && f.targetId && (
                          <input
                            className={s.shareInput}
                            placeholder="Anteil"
                            value={f.share}
                            onChange={(e) => patchForm(c.id, { share: e.target.value })}
                          />
                        )}
                      </div>

                      {f.pickerOpen && !f.targetId && (
                        <div className={s.pickerBox}>
                          <RelationContactPicker
                            defaultType="Firma"
                            selected={null}
                            excludeContactIds={[c.id]}
                            onSelect={(picked) => {
                              if (!picked?.id) { patchForm(c.id, { pickerOpen: false }); return; }
                              const label = picked.displayName || `${picked.lastname ?? ""} ${picked.suffix ?? ""}`.trim() || "Firma";
                              patchForm(c.id, { targetId: Number(picked.id), targetLabel: label, pickerOpen: false });
                            }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </>
        )}

        <div className={s.saveBar}>
          {error && <span className={s.errText}>{error}</span>}
          <span className={s.saveInfo}>{dirtyCount > 0 ? `${dirtyCount} Änderung${dirtyCount === 1 ? "" : "en"}` : "Keine Änderungen"}</span>
          <button className={s.btnPrimary} disabled={saving || dirtyCount === 0} onClick={onSave}>
            {saving ? "Speichert…" : "Speichern"}
          </button>
        </div>
      </div>
    );
  }

  // ── Übersicht ────────────────────────────────────────────────────────────────
  return (
    <div className={s.root}>
      <div className={s.bar}>
        <BuildingMultiple20Regular className={s.barIcon} fontSize={26} />
        <div>
          <h1 className={s.title}>Unternehmensstrukturen</h1>
          <div className={s.subtitle}>Firmen an gleicher Anschrift zu Konzern- und Beteiligungsstrukturen verknüpfen.</div>
        </div>
      </div>

      <div className={s.filters}>
        <div className={s.seg}>
          <button className={mergeClasses(s.segBtn, status === "open" && s.segOn)} onClick={() => setStatus("open")}>Offen</button>
          <button className={mergeClasses(s.segBtn, status === "erledigt" && s.segOn)} onClick={() => setStatus("erledigt")}>Erledigt</button>
        </div>
        <div className={s.seg}>
          <button className={mergeClasses(s.segBtn, sort === "count" && s.segOn)} onClick={() => setSort("count")}>Nach Anzahl</button>
          <button className={mergeClasses(s.segBtn, sort === "street" && s.segOn)} onClick={() => setSort("street")}>Nach Ort</button>
        </div>
        <select className={s.select} value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">Alle Teams</option>
          <option value="none">Ohne Team</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>{t.name || t.id}</option>
          ))}
        </select>
        <div className={s.searchWrap}>
          <Search20Regular className={s.searchIcon} />
          <input className={s.search} placeholder="Adresse oder Firma suchen…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div className={s.count}>{total} Adress-Cluster</div>

      <InfiniteScroll
        dataLength={rows.length}
        next={() => loadList(false)}
        hasMore={rows.length < total}
        loader={<div className={s.center}><Spinner size="tiny" /></div>}
        scrollThreshold={0.9}
      >
        {rows.map((r) => (
          <div key={r.structureKey} className={s.card} onClick={() => openDetail(r.structureKey)}>
            <div className={s.cardMain}>
              <div className={s.cardAddr}>{r.strasse || "—"}</div>
              <div className={s.cardCity}>{[r.plz, r.ort].filter(Boolean).join(" ")}</div>
              {r.names?.length > 0 && <div className={s.cardNames}>{r.names.join(" · ")}</div>}
            </div>
            <span className={mergeClasses(s.pill, r.hasOpen ? s.pillOpen : s.pillDone)}>{r.hasOpen ? "Offen" : "Erledigt"}</span>
            <span className={s.badge}><BuildingMultiple20Regular fontSize={15} />{r.companyCount}</span>
            <ChevronRight20Regular style={{ color: REFINED.ink3, flexShrink: 0 }} />
          </div>
        ))}
        {!loading && rows.length === 0 && (
          <div className={s.empty}>Keine Adress-Cluster gefunden.</div>
        )}
      </InfiniteScroll>
    </div>
  );
}
